import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api, type SSLCert, type Node } from '../lib/api'
import { PageHeader, Button, Table, Modal, Field, Input, Select, Textarea, ErrorBox, Badge } from '../components/ui'
import { IconPlus } from '../components/icons'
import { RowMenu } from '../components/RowMenu'
import { TaskProgress } from '../components/TaskProgress'
import { FilterBar, FilterSelect, SearchInput } from '../components/Filters'

const TYPE_TONE: Record<string, 'green'|'blue'|'yellow'|'purple'> = {
  letsencrypt: 'green', custom: 'blue', selfsigned: 'yellow',
}
const TYPE_RU: Record<string, string> = {
  letsencrypt: "Let's Encrypt", custom: 'загруженный', selfsigned: 'самоподписанный',
}

export function CertificatesPage() {
  const qc = useQueryClient()
  const [mode, setMode] = useState<null | 'upload' | 'selfsigned'>(null)
  const [taskId, setTaskId] = useState<string | null>(null)

  const { data: certs = [] } = useQuery({ queryKey: ['certificates'], queryFn: async () => (await api.get<SSLCert[]>('/certificates')).data })
  const { data: nodes = [] } = useQuery({ queryKey: ['nodes'], queryFn: async () => (await api.get<Node[]>('/nodes')).data })
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]))

  const [fNode, setFNode] = useState('')
  const [fType, setFType] = useState<'all'|'letsencrypt'|'custom'|'selfsigned'>('all')
  const [fSearch, setFSearch] = useState('')
  const filtered = useMemo(() => {
    let list = certs
    if (fNode) list = list.filter(c => c.node_id === fNode)
    if (fType !== 'all') list = list.filter(c => c.type === fType)
    if (fSearch) {
      const q = fSearch.toLowerCase()
      list = list.filter(c => c.name.toLowerCase().includes(q) ||
        (c.subject || '').toLowerCase().includes(q) ||
        (c.domains || []).some(d => d.toLowerCase().includes(q)))
    }
    return list
  }, [certs, fNode, fType, fSearch])

  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/certificates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['certificates'] }),
  })
  const renew = useMutation({
    mutationFn: async (id: number) => (await api.post<{ task_id: string }>(`/certificates/${id}/renew`)).data,
    onSuccess: (r) => setTaskId(r.task_id),
  })
  const doExport = async (c: SSLCert) => {
    const r = await api.get<{ fullchain_pem: string; privkey_pem: string }>(`/certificates/${c.id}/export`)
    const blob = new Blob([
      `# ${c.name} (fullchain)\n${r.data.fullchain_pem}\n\n# privkey\n${r.data.privkey_pem}`
    ], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `${c.name}.pem`; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <PageHeader
        title="SSL-сертификаты"
        subtitle="Let’s Encrypt для сайтов выпускается автоматически. Здесь можно загрузить свой или сгенерировать самоподписанный."
        actions={
          <>
            <Button variant="secondary" onClick={() => setMode('selfsigned')}>Сгенерировать self-signed</Button>
            <Button onClick={() => setMode('upload')}><IconPlus /> Загрузить сертификат</Button>
          </>
        }
      />
      <FilterBar>
        <SearchInput value={fSearch} onChange={setFSearch} placeholder="Имя / домен…" className="min-w-[240px] flex-1" />
        <FilterSelect value={fNode} onChange={setFNode} options={[
          { value: '', label: 'Все ноды' },
          ...nodes.map(n => ({ value: n.id, label: n.name })),
        ]} />
        <FilterSelect value={fType} onChange={setFType} options={[
          { value: 'all',         label: 'Все типы' },
          { value: 'letsencrypt', label: "Let's Encrypt" },
          { value: 'custom',      label: 'Загруженные' },
          { value: 'selfsigned',  label: 'Самоподписанные' },
        ]} />
      </FilterBar>
      <Table<SSLCert>
        rows={filtered}
        empty={certs.length === 0 ? 'Нет сохранённых сертификатов.' : 'Ничего не найдено.'}
        cols={[
          { key: 'n', label: 'Имя', render: (c) => <span className="font-mono font-medium">{c.name}</span> },
          { key: 't', label: 'Тип', render: (c) => <Badge tone={TYPE_TONE[c.type] || 'neutral'}>{TYPE_RU[c.type] || c.type}</Badge> },
          { key: 'd', label: 'Домены', render: (c) => (
            <div className="text-[12px] text-neutral-700">
              {c.domains?.length ? c.domains.join(', ') : (c.subject || '—')}
            </div>
          )},
          { key: 'nd', label: 'Нода', render: (c) => <span className="text-[13px] text-neutral-600">{nodeMap[c.node_id]?.name || c.node_id.slice(0, 8)}</span> },
          { key: 'v', label: 'Действителен до', render: (c) => (
            <span className="text-[12px] text-neutral-600">
              {c.valid_until ? new Date(c.valid_until).toLocaleDateString('ru-RU') : '—'}
            </span>
          )},
          { key: 'act', label: '', w: '1%', render: (c) => (
            <div className="flex justify-end">
              <RowMenu items={[
                ...(c.type === 'letsencrypt' ? [{ label: 'Продлить LE', onClick: () => renew.mutate(c.id) }] : []),
                { label: 'Скачать PEM', onClick: () => doExport(c) },
                { divider: true, label: '', onClick: () => {} },
                { label: 'Удалить', danger: true,
                  onClick: () => confirm(`Удалить сертификат ${c.name}?`) && del.mutate(c.id) },
              ]} />
            </div>
          )},
        ]}
      />
      {mode === 'upload' && <UploadModal nodes={nodes} onClose={() => setMode(null)}
        onTask={(t) => { setMode(null); setTaskId(t); qc.invalidateQueries({ queryKey: ['certificates'] }) }} />}
      {mode === 'selfsigned' && <SelfsignedModal nodes={nodes} onClose={() => setMode(null)}
        onTask={(t) => { setMode(null); setTaskId(t); qc.invalidateQueries({ queryKey: ['certificates'] }) }} />}
      {taskId && <TaskProgress taskId={taskId}
        onClose={() => { setTaskId(null); qc.invalidateQueries({ queryKey: ['certificates'] }) }} />}
    </>
  )
}

function UploadModal({ nodes, onClose, onTask }: { nodes: Node[]; onClose: () => void; onTask: (id: string) => void }) {
  const [f, setF] = useState({ node_id: nodes[0]?.id || '', name: '', fullchain_pem: '', privkey_pem: '' })
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => (await api.post('/certificates/upload', f)).data,
    onSuccess: (r: any) => onTask(r.task_id),
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Загрузить сертификат" size="lg">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Нода">
          <Select value={f.node_id} onChange={(e) => setF({ ...f, node_id: e.target.value })}>
            {nodes.map(n => <option key={n.id} value={n.id}>{n.name} — {n.hostname}</option>)}
          </Select>
        </Field>
        <Field label="Имя (для идентификации)" hint="Например: example-com или my-wildcard">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="example-com" required autoFocus />
        </Field>
        <Field label="Полная цепочка (fullchain.pem)" hint="BEGIN CERTIFICATE...END CERTIFICATE. Обычно включает промежуточные.">
          <Textarea value={f.fullchain_pem} onChange={(e) => setF({ ...f, fullchain_pem: e.target.value })}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    className="font-mono text-[11px] min-h-[140px]" required />
        </Field>
        <Field label="Приватный ключ (privkey.pem)">
          <Textarea value={f.privkey_pem} onChange={(e) => setF({ ...f, privkey_pem: e.target.value })}
                    placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                    className="font-mono text-[11px] min-h-[140px]" required />
        </Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Загружаем…' : 'Загрузить'}</Button>
        </div>
      </form>
    </Modal>
  )
}

function SelfsignedModal({ nodes, onClose, onTask }: { nodes: Node[]; onClose: () => void; onTask: (id: string) => void }) {
  const [f, setF] = useState({ node_id: nodes[0]?.id || '', domain: '', aliases: '' })
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => (await api.post('/certificates/selfsigned', {
      node_id: f.node_id, domain: f.domain,
      aliases: f.aliases.split(/[\s,]+/).filter(Boolean),
    })).data,
    onSuccess: (r: any) => onTask(r.task_id),
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Сгенерировать самоподписанный">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <p className="text-[13px] text-neutral-600">
          Создаст RSA-2048 сертификат на 10 лет. Подходит для внутренних доменов, тестирования.
          Браузеры покажут предупреждение о недоверенном сертификате.
        </p>
        <Field label="Нода">
          <Select value={f.node_id} onChange={(e) => setF({ ...f, node_id: e.target.value })}>
            {nodes.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
          </Select>
        </Field>
        <Field label="Домен (CN)"><Input value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} placeholder="example.com" required autoFocus /></Field>
        <Field label="Дополнительные имена (SAN)" hint="Через запятую">
          <Input value={f.aliases} onChange={(e) => setF({ ...f, aliases: e.target.value })} placeholder="www.example.com, api.example.com" />
        </Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Генерируем…' : 'Сгенерировать'}</Button>
        </div>
      </form>
    </Modal>
  )
}
